/**
 * CoCaptainSection — manages the crew list (co-captains, pending invites)
 * for a single vessel. Owner + admin can invite/revoke; viewers + editors
 * can only see themselves and self-leave.
 *
 * Extracted from VesselSettings.jsx (Phase 2 frontend refactor, Feb 6, 2026)
 * to keep that file focused on engine/tank/category configuration.
 */
import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Mail, LogOut } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";


const ROLE_LABEL = {
    owner: "Owner",
    admin: "Admin",
    editor: "Editor",
    viewer: "Viewer",
};

const ROLE_HINT = {
    viewer: "Read-only — can see entries but can't add, edit, or delete.",
    editor: "Full add / edit / delete on entries. Can't manage other crew.",
    admin: "Editor + can invite or remove other co-captains.",
};


export default function CoCaptainSection({ vesselId, viewerRole, currentUserId }) {
    const canManage = viewerRole === "owner" || viewerRole === "admin";
    const isCoCaptain = viewerRole !== "owner";
    const [captains, setCaptains] = useState([]);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [note, setNote] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("editor");
    const [inviting, setInviting] = useState(false);
    const [resendingId, setResendingId] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/vessels/${vesselId}/co-captains`);
            setCaptains(data.captains || []);
            setPending(data.pending_invites || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load crew list.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vesselId) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId]);

    const sendInvite = async (e) => {
        e?.preventDefault?.();
        const email = inviteEmail.trim().toLowerCase();
        if (!email) return;
        setInviting(true);
        setErr("");
        setNote("");
        try {
            const { data } = await api.post(`/vessels/${vesselId}/invite`, { email, role: inviteRole });
            setInviteEmail("");
            if (data.auto_granted) {
                setNote(`${email} already had an account — access granted instantly as ${inviteRole}.`);
            } else {
                setNote(`Invite sent to ${email}. They'll get an email with a join link.`);
            }
            await refresh();
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't send invite.");
        } finally {
            setInviting(false);
        }
    };

    const removeCaptain = async (capUserId, label) => {
        const isSelf = capUserId === currentUserId;
        const msg = isSelf
            ? "Leave this vessel? You'll lose access until the owner re-invites you."
            : `Remove ${label} from this vessel?`;
        if (!window.confirm(msg)) return;
        setErr("");
        try {
            await api.delete(`/vessels/${vesselId}/co-captains/${capUserId}`);
            if (isSelf) {
                // Co-captain left — redirect to home
                window.location.assign("/app");
                return;
            }
            await refresh();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove crew member.");
        }
    };

    const revokeInvite = async (inviteId, email) => {
        if (!window.confirm(`Revoke the pending invitation for ${email}?`)) return;
        setErr("");
        try {
            await api.delete(`/vessels/${vesselId}/invitations/${inviteId}`);
            await refresh();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't revoke invite.");
        }
    };

    const resendInvite = async (inviteId, email) => {
        setErr("");
        setNote("");
        setResendingId(inviteId);
        try {
            await api.post(`/vessels/${vesselId}/invitations/${inviteId}/resend`);
            setNote(`Invite re-sent to ${email}. The previous link is now invalid.`);
            await refresh();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't resend invite.");
        } finally {
            setResendingId(null);
        }
    };

    return (
        <section data-testid="co-captains-section" className="bm-card" style={{ padding: 22, marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <Users size={18} color="var(--bm-gold)" />
                <h2 className="bm-serif" style={{ fontSize: 22, margin: 0, fontWeight: 500 }}>Co-Captains</h2>
            </div>
            <p style={{ color: "var(--bm-text-muted)", margin: "0 0 16px", fontSize: 13 }}>
                Share this vessel's log with crew, partners, or your mechanic. Each person uses their own Bosun's Mate login.
            </p>

            {err && <div data-testid="co-captain-err" style={{ color: "var(--bm-bad)", marginBottom: 10, fontSize: 13 }}>{err}</div>}
            {note && <div data-testid="co-captain-note" style={{ color: "var(--bm-good)", marginBottom: 10, fontSize: 13 }}>{note}</div>}

            {/* Invite form (owner / admin only) */}
            {canManage && (
                <form data-testid="invite-form" onSubmit={sendInvite} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
                    <input
                        data-testid="invite-email-input"
                        type="email"
                        className="bm-input"
                        placeholder="crewmate@example.com"
                        value={inviteEmail}
                        onChange={(ev) => setInviteEmail(ev.target.value)}
                        required
                        style={{ flex: "1 1 220px", minWidth: 0 }}
                    />
                    <select
                        data-testid="invite-role-select"
                        className="bm-input"
                        value={inviteRole}
                        onChange={(ev) => setInviteRole(ev.target.value)}
                        style={{ width: 130 }}
                    >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                    </select>
                    <button
                        type="submit"
                        data-testid="invite-send-btn"
                        className="bm-btn bm-btn-gold"
                        disabled={inviting || !inviteEmail.trim()}
                    >
                        <Mail size={14} /> {inviting ? "Sending…" : "Send invite"}
                    </button>
                </form>
            )}
            {canManage && (
                <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: -10, marginBottom: 16 }}>
                    <strong style={{ color: "var(--bm-text)" }}>{ROLE_LABEL[inviteRole]}:</strong> {ROLE_HINT[inviteRole]}
                </div>
            )}

            {loading ? (
                <div style={{ color: "var(--bm-text-muted)", fontSize: 13, padding: "10px 0" }}>Loading crew…</div>
            ) : (
                <>
                    {/* Active captains */}
                    <div data-testid="captains-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {captains.map((c) => {
                            const isSelf = c.user_id === currentUserId;
                            const isOwnerRow = c.role === "owner";
                            const canRemove = !isOwnerRow && (canManage || isSelf);
                            return (
                                <div
                                    key={c.user_id}
                                    data-testid={`captain-row-${c.user_id}`}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "10px 12px",
                                        background: "rgba(8,22,40,0.5)",
                                        border: "1px solid var(--bm-border-soft)",
                                        borderRadius: "var(--bm-r)",
                                    }}
                                >
                                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(212,181,114,0.12)", display: "grid", placeItems: "center", fontWeight: 600, color: "var(--bm-gold)", fontSize: 13 }}>
                                        {(c.name || c.email || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 14, color: "var(--bm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {c.name || c.email}
                                            {isSelf && <span style={{ color: "var(--bm-text-muted)", fontWeight: 400 }}> · You</span>}
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</div>
                                    </div>
                                    <span
                                        data-testid={`captain-role-${c.user_id}`}
                                        className="bm-tag"
                                        style={{
                                            fontSize: 11,
                                            padding: "3px 9px",
                                            color: isOwnerRow ? "var(--bm-gold)" : "var(--bm-text)",
                                            borderColor: isOwnerRow ? "var(--bm-gold-dim)" : "var(--bm-border)",
                                            textTransform: "capitalize",
                                        }}
                                    >
                                        {ROLE_LABEL[c.role] || c.role}
                                    </span>
                                    {canRemove && (
                                        <button
                                            type="button"
                                            data-testid={`captain-remove-${c.user_id}`}
                                            onClick={() => removeCaptain(c.user_id, c.name || c.email)}
                                            className={isSelf ? "bm-btn bm-btn-ghost" : "bm-btn bm-btn-danger"}
                                            style={{ padding: "5px 9px", fontSize: 12 }}
                                            title={isSelf ? "Leave this vessel" : "Remove crew member"}
                                        >
                                            {isSelf ? <><LogOut size={12} /> Leave</> : <Trash2 size={12} />}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Pending invites */}
                    {pending.length > 0 && (
                        <>
                            <div className="bm-label" style={{ marginTop: 18, marginBottom: 8, color: "var(--bm-gold)", fontSize: 11, letterSpacing: "0.18em" }}>
                                PENDING INVITES
                            </div>
                            <div data-testid="pending-invites-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {pending.map((inv) => (
                                    <div
                                        key={inv.id}
                                        data-testid={`pending-invite-${inv.id}`}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            padding: "10px 12px",
                                            background: "rgba(8,22,40,0.35)",
                                            border: "1px dashed var(--bm-border-soft)",
                                            borderRadius: "var(--bm-r)",
                                        }}
                                    >
                                        <Mail size={16} color="var(--bm-text-dim)" />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 14, color: "var(--bm-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.email}</div>
                                            <div style={{ fontSize: 12, color: "var(--bm-text-muted)" }}>
                                                Invited as {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                        {canManage && (
                                            <>
                                                <button
                                                    type="button"
                                                    data-testid={`invite-resend-${inv.id}`}
                                                    onClick={() => resendInvite(inv.id, inv.email)}
                                                    disabled={resendingId === inv.id}
                                                    className="bm-btn bm-btn-ghost"
                                                    style={{ padding: "5px 9px", fontSize: 12 }}
                                                    title="Resend invite email"
                                                >
                                                    {resendingId === inv.id ? "Sending…" : "Resend"}
                                                </button>
                                                <button
                                                    type="button"
                                                    data-testid={`invite-revoke-${inv.id}`}
                                                    onClick={() => revokeInvite(inv.id, inv.email)}
                                                    className="bm-btn bm-btn-ghost"
                                                    style={{ padding: "5px 9px", fontSize: 12 }}
                                                    title="Revoke invite"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {!canManage && isCoCaptain && (
                <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginTop: 14 }}>
                    Only the vessel owner or an admin can invite or remove crew.
                </div>
            )}
        </section>
    );
}
